import { css } from "@emotion/react"
import { Link } from "react-router"
import {
  Badge,
  Icon,
  RoutingAwareDangerousHTML,
} from "source/shared/components"
import type { Announcement } from "./types"

function isExternalLink(url: string) {
  return !url.startsWith("/")
}

const Wrapper = ({
  link,
  children,
}: {
  link?: string | null
  children: React.ReactNode
}) => {
  switch (true) {
    case link && isExternalLink(link):
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          css={[styles.listItem, styles.listItemLink]}
        >
          {children}
        </a>
      )
    case link && !isExternalLink(link):
      return (
        <Link to={link} css={[styles.listItem, styles.listItemLink]}>
          {children}
        </Link>
      )
    default:
      return <div css={styles.listItem}>{children}</div>
  }
}

export const AnnouncementListItem = ({
  announcement,
  showImage = false,
  showFeaturedBadge = true,
}: {
  announcement: Announcement
  showImage?: boolean
  showFeaturedBadge?: boolean
}) => {
  const { title, summary, image, link, featured } = announcement.attributes

  return (
    <Wrapper link={link}>
      {showImage && image && (
        <img
          loading="lazy"
          src={image.attributes.variants.medium}
          alt={title}
          css={styles.listItemImage}
        />
      )}
      <div css={styles.listItemInfo}>
        <h3 css={styles.listItemTitle}>
          {title}
          {link && (
            <Icon
              className="fs-5 ml-4p mt-3p"
              symbol="general#right-chevron"
              aria-hidden="true"
              css={{ color: "var(--d-icon-color-dim)" }}
            />
          )}
        </h3>
        {summary && (
          <RoutingAwareDangerousHTML
            html={summary}
            styles={styles.listItemDescription}
            className="announcement-summary"
          />
        )}
        {featured && showFeaturedBadge && <Badge.Featured />}
      </div>
    </Wrapper>
  )
}

const styles = {
  listItem: css`
    max-width: 700px;
    width: 100%;
    margin-inline: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media (min-width: 601px) {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
    }

    [data-preformatted-content] p:last-child {
      margin-bottom: 0;
    }
  `,
  listItemImage: css`
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    border-radius: 8px;

    @media (min-width: 601px) {
      grid-column: 3 / span 1;
    }
  `,
  listItemInfo: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    @media (min-width: 601px) {
      grid-column: span 2;
      grid-row: 1 / span 1;

      &:only-child {
        grid-column: 1 / -1;
      }
    }
  `,
  listItemTitle: css`
    font-weight: 600;
    font-size: var(--d-font-size-md);
    color: var(--d-text-color-headline);
  `,
  listItemDescription: css`
    font-size: 0.875rem;
    color: var(--d-text-color-secondary);
    line-height: 1.4;

    .page-block & {
      font-size: var(--d-font-size-md);
    }

    p {
      margin-bottom: 0.5rem;
    }

    p:last-child {
      margin-bottom: 0;
    }
  `,
  listItemLink: css`
    text-decoration: none;
    color: inherit;
    &:hover h3 {
      color: var(--d-text-color-interaction);
    }
  `,
}
